import { AppKind } from "../../components/SerializedApp";
import { asBoolean, asString } from "../../computation-model/data";
import {
    ContainerStyle,
    HeroStyle,
    WireDataGridColumnKind,
    HeroSize,
    DefaultImage,
    HeroImageEffect,
    CardStyle,
    SeparatorSize,
    UIAlignment,
    ButtonBarSize,
    ButtonBarBackground,
} from "../../wire";
import { WireValueGetter } from ".";
import { ArrayScreenFormat, isPrimitiveType } from "../description";
import {
    component,
    properties,
    arrayContent,
    DescriptionOfProperties,
    DescriptionOfComponent,
    DescriptionOfArrayContentInlineList,
} from "./fluent-components-support";
import { getArrayProperty, getEnumProperty, getStringProperty } from "./property-utils";
import { PropertySection } from "./property-sections";
import { WireComponentKind } from "../../wire/component-kind";
import { Corners } from "./image-types";
import { Appearance } from "./button-appearance";

const section = component.withinGroup("Sections");

const componentActionSection = { name: "Component Action", order: 0 };
const primaryActionSection = { name: "Primary", order: 1 };
const secondaryActionSection = { name: "Secondary", order: 2 };
const tertiaryActionSection = { name: "Tertiary", order: 3 };

const heroSizes = [HeroSize.Small, HeroSize.Medium, HeroSize.Large, HeroSize.XLarge];

function makeActionWithTitleProperties<TParent>() {
    return properties.withParent<TParent>().withText("title").withAction("action");
}

const Button = section
    .withDisplayName("Button")
    .withKind(WireComponentKind.Button)
    .withProperties(
        properties
            .withText("title")
            .withAction("action")
            .withText("description")
            .withEnum("appearance", [
                Appearance.Filled,
                Appearance.Transparent,
                Appearance.Bordered,
                Appearance.Simple,
                Appearance.FullBleed,
                Appearance.Floating,
            ])
    );

const Hint = section
    .withDisplayName("Hint")
    .withKind(WireComponentKind.Hint)
    .withProperties(properties.withText("description").withText("actionTitle").withAction("action"));

const HeroSection = section
    .withDisplayName("Hero")
    .withKind(WireComponentKind.Hero)
    .withProperties(
        properties
            .withText("title")
            .withText("subtitle")
            .withImage("image")
            .withText("titleEmphasized", { displayName: "Emphasis" })
            .withEnum("size", heroSizes)
            .withEnum(
                "style",
                [HeroStyle.Card, HeroStyle.Accent, HeroStyle.Dark, HeroStyle.White, HeroStyle.Image, HeroStyle.None],
                { displayName: "Background", propertySection: PropertySection.Design }
            )
            .withImage("backgroundImage", {
                propertySection: PropertySection.Design,
                when: d => getEnumProperty(d.style) === HeroStyle.Image,
            })
            .withEnum("backgroundEffect", [HeroImageEffect.None, HeroImageEffect.Darken, HeroImageEffect.Lighten], {
                displayName: "Background Effect",
                propertySection: PropertySection.Design,
                when: d => getEnumProperty(d.style) === HeroStyle.Image,
            })
            .withSwitch("withBlur", false, {
                propertySection: PropertySection.Design,
                when: d => getEnumProperty(d.style) === HeroStyle.Image,
            })
            .withEnum("imageCorners", [Corners.Square, Corners.Rounded])
            .withEnum("imageDefault", [DefaultImage.None, DefaultImage.Avatar, DefaultImage.Icon])
            .withText("actionTitle", { propertySection: primaryActionSection })
            .withAction("action", { propertySection: primaryActionSection })
            .withText("secondaryActionTitle", {
                propertySection: secondaryActionSection,
                displayName: "Action Title",
            })
            .withAction("secondaryAction", { propertySection: secondaryActionSection })
        // .withArray("menuActions", makeActionWithTitleProperties())
    );
export type HeroSectionDescription = DescriptionOfComponent<typeof HeroSection>;

const DynamicButtonBar = section
    .withDisplayName("Button Bar")
    .withKind(WireComponentKind.DynamicButtonBar)
    .withProperties(
        properties
            .withArray("buttons", makeActionWithTitleProperties(), {
                propertySection: PropertySection.Action,
            })
            .withEnum("alignment", [UIAlignment.Start, UIAlignment.Center, UIAlignment.End])
            .withText("title")
            .withText("description")
            .withEnum("size", [ButtonBarSize.Auto, ButtonBarSize.Wide])
            .withEnum("background", [
                ButtonBarBackground.Card,
                ButtonBarBackground.Accent,
                ButtonBarBackground.Soft,
                ButtonBarBackground.None,
            ])
    );

const Breadcrumbs = section
    .withDisplayName("Breadcrumbs")
    .withKind(WireComponentKind.Breadcrumbs)
    .withProperties(
        properties
            .withText("currentPageTitle")
            .withArray("crumbs", makeActionWithTitleProperties())
            .withAction("backAction", { propertySection: { name: "Back action", order: 0 } })
    );

const ContactForm = section
    .withDisplayName("Contact Form")
    .withKind(WireComponentKind.ContactForm)
    .withProperties(
        properties
            .withText("title", { propertySection: PropertySection.Description })
            .withText("subtitle", { propertySection: PropertySection.Description })
            .withImage("image", { propertySection: PropertySection.Description })
            .withText("contactAddress", { propertySection: PropertySection.Description })
            .withText("contactEmail", { propertySection: PropertySection.Description })
            .withText("contactPhone", { propertySection: PropertySection.Description })
    )
    .withFormProperties(properties.withText("name").withText("email").withText("phone").withText("message"));

const Container = section
    .withDisplayName("Container")
    .withKind(WireComponentKind.Container)
    .withProperties(
        properties.withEnum("containerStyle", [ContainerStyle.Default, ContainerStyle.Highlight, ContainerStyle.Accent])
    )
    .withIsContainer();

const Fields = section
    .withDisplayName("Fields")
    .withKind(WireComponentKind.Fields)
    // FIXME: The value needs to come with the column type
    .withProperties(properties.withText("title").withArray("fields", properties.withText("name").withText("value")));

const Separator = section
    .withDisplayName("Separator")
    .withKind(WireComponentKind.Separator)
    .withProperties(
        properties
            .withEnum("size", [SeparatorSize.Small, SeparatorSize.Medium, SeparatorSize.Large])
            .withSwitch("drawLine", false)
    );

export const components = {
    // NOTE: These names are used as the component kinds.  Do not change them,
    // or existing apps will stop working.
    Button,
    Hint,
    HeroSection,
    ContactForm,
    DynamicButtonBar,
    Breadcrumbs,
    Container,
    Fields,
    Separator,
};

const CardCollection = arrayContent
    .withFormat(ArrayScreenFormat.CardCollection)
    .withComponentProperties(
        properties
            .withEnum(
                "cardStyle",
                [
                    { value: CardStyle.Card, label: "Card", icon: "componentInlineList" },
                    { value: CardStyle.Article, label: "Article", icon: "componentInlineList" },
                    { value: CardStyle.List, label: "List", icon: "componentInlineList" },
                    { value: CardStyle.Table, label: "Table", icon: "componentInlineList" },
                ],
                {
                    propertySection: PropertySection.Style,
                    enumVisual: "large-images",
                }
            )
            .withText("componentTitle", { propertySection: PropertySection.Title })
            .withText("componentActionTitle", { propertySection: componentActionSection })
            .withAction("componentAction", { propertySection: componentActionSection })
    )
    .withItemProperties(p =>
        p
            .withText("title")
            .withText("subtitle")
            .withText("emphasis")
            .withText("body", { when: (_d, c) => getEnumProperty(c?.cardStyle) === CardStyle.Article })
            .withImage("image")
            .withImage("heroImage", {
                displayName: "Hero Image",
            })
            .withAction("action", { propertySection: primaryActionSection })
            .withText("secondaryActionTitle", {
                displayName: "Action Title",
                propertySection: secondaryActionSection,
            })
            .withAction("secondaryAction", { propertySection: secondaryActionSection })
            .withText("tertiaryActionTitle", {
                displayName: "Action Title",
                propertySection: tertiaryActionSection,
            })
            .withAction("tertiaryAction", { propertySection: tertiaryActionSection })
    )
    .withSpecialCase({
        name: "Collection",
        analyticsName: "cards-collection",
        description: "A collection of cards",
        // FIXME: Proper image
        img: "componentCharts",
        group: "Lists",
        appKinds: AppKind.Page,
    });
export type CardCollectionComponentDescription = DescriptionOfArrayContentInlineList<typeof CardCollection>;

const columnProperties = properties.withText("title", { allowColumn: false }).withText("value");
type ColumnDescription = DescriptionOfProperties<typeof columnProperties>;

const DataGrid = arrayContent
    .withFormat(ArrayScreenFormat.DataGrid)
    .withItemProperties(p => p.withArray("columns", columnProperties))
    .withSpecialCase({
        name: "Data Grid",
        analyticsName: "data-grid",
        description: "A grid of data",
        // FIXME: Proper image
        img: "componentCharts",
        group: "Lists",
        appKinds: AppKind.Page,
        isLegacy: true,
    })
    .withInflator((ib, desc) => {
        const columnDescs = getArrayProperty<ColumnDescription>(desc.columns);
        if (columnDescs === undefined || columnDescs.length === 0) return undefined;

        const columns: { title: string; getter: WireValueGetter; kind: WireDataGridColumnKind }[] = [];

        for (const c of columnDescs) {
            const title = getStringProperty(c.title) ?? "";
            const [getter, type] = ib.getValueGetterForProperty(c.value, true);
            if (!isPrimitiveType(type)) continue;
            const kind = type.kind === "boolean" ? "boolean" : type.kind === "image-uri" ? "image-uri" : "string";
            columns.push({ title, getter, kind });
        }

        return hb => {
            const rows = hb.tableScreenContext;
            if (rows.length === 0) return undefined;

            return {
                kind: WireComponentKind.List,
                format: ArrayScreenFormat.DataGrid,
                componentTitle: "",
                columns: columns.map(c => ({ title: c.title, kind: c.kind })),
                items: rows.map(row => {
                    const rhb = hb.makeHydrationBackendForRow(row);

                    const values: (string | boolean)[] = [];
                    for (const c of columns) {
                        let v = c.getter(rhb);
                        if (c.kind === "boolean") {
                            v = asBoolean(v);
                        } else {
                            v = asString(v);
                        }
                        values.push(v);
                    }
                    return values;
                }),
            };
        };
    });

export const arrayContents = { CardCollection, DataGrid };
